import React, { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import SimplePeer from 'simple-peer';
import { User } from 'oidc-client-ts';
import AuthServiceType, { IAuthService } from '@murphy-frontend/common/interfaces/IAuthService';
import container from './../../../../inversify.config';
import { baseURL } from '../../../common/variables';

const VideoRoom: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [usersInitialized, setUsersInitialized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const peersRef = useRef<Map<string, SimplePeer.Instance>>(new Map());
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideosRef = useRef<Map<string, HTMLVideoElement | HTMLDivElement>>(new Map()); // Allow div for audio-only
  const localStreamRef = useRef<MediaStream | null>(null);
  const [connectedUsers, setConnectedUsers] = useState<Map<string, string>>(new Map());
  const [messages, setMessages] = useState<{ senderId: string; message: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const signalQueueRef = useRef<Map<string, SimplePeer.SignalData[]>>(new Map());
  const peerCreationPendingRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const authService = container.get<IAuthService>(AuthServiceType.IAuthService);
    const userStr = authService.getUserStorageString();
    if (userStr) {
      const currentUser = User.fromStorageString(userStr);
      setUser(currentUser);
      console.log('Loaded user:', currentUser);
    } else {
      console.error('User not authenticated');
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const hubUrl = `${baseURL}/VideoRoomHub`;
    console.log('Connecting to hub at:', hubUrl);
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => user.access_token || '',
        withCredentials: true,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    conn.on('ReceiveSignal', (senderId: string, signalData: string) => {
      console.log('ReceiveSignal from:', senderId, 'Data:', signalData.substring(0, 50) + (signalData.length > 50 ? '...' : ''));
      if (senderId === conn.connectionId) {
        console.log('Ignoring self-signal');
        return;
      }
      try {
        const parsed = JSON.parse(signalData);
        console.log('Parsed signal type:', parsed.type);

        let peer = peersRef.current.get(senderId);
        if (!peer) {
          if (parsed.type === 'offer') {
            console.warn('No peer exists and received offer => creating non-initiator peer');
            if (localStreamRef.current) {
              peer = createPeer(senderId, false);
              peersRef.current.set(senderId, peer);
            } else {
              console.error('Local stream not ready, cannot create peer');
              return;
            }
          } else {
            console.error('Received candidate/answer but no peer exists. Queuing signal:', parsed.type);
            signalQueueRef.current.set(senderId, (signalQueueRef.current.get(senderId) || []).concat([parsed]));
            return;
          }
        }

        peer.signal(parsed);

        const queuedSignals = signalQueueRef.current.get(senderId) || [];
        if (queuedSignals.length > 0) {
          console.log(`Processing ${queuedSignals.length} queued signals for ${senderId}`);
          queuedSignals.forEach(signal => peer.signal(signal));
          signalQueueRef.current.delete(senderId);
        }
      } catch (err) {
        console.error('Error parsing signal:', err);
      }
    });

    conn.on('ReceiveChatMessage', (senderId: string, message: string) => {
      setMessages(prev => [...prev, { senderId, message }]);
    });

    conn.on('UserJoined', (connId: string, userName: string) => {
      console.log('UserJoined event:', connId, userName);
      setConnectedUsers(prev => {
        const newMap = new Map(prev);
        newMap.set(connId, userName);
        return newMap;
      });
      if (isConnected && usersInitialized && localStreamRef.current && connId !== conn.connectionId) {
        console.log('New user joined, notifying backend of readiness');
        conn.invoke('SetUserReady').catch(err => console.error('Error setting user ready on join:', err));
      }
    });

    conn.on('UserLeft', (connId: string, userName: string) => {
      console.log('UserLeft event:', connId, userName);
      setConnectedUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(connId);
        return newMap;
      });
      const peer = peersRef.current.get(connId);
      if (peer) {
        peer.destroy();
        peersRef.current.delete(connId);
        const element = remoteVideosRef.current.get(connId);
        if (element) {
          element.remove();
          remoteVideosRef.current.delete(connId);
        }
      }
    });

    conn.on('StartConnection', (otherConnId, isInitiator = true) => {
      console.log(`StartConnection received for ${otherConnId}, initiator = ${isInitiator}, connection state: ${conn.state}`);
      if (peersRef.current.has(otherConnId)) {
        console.log(`Peer already exists for ${otherConnId}, ignoring StartConnection`);
        return;
      }
      if (localStreamRef.current && conn.state === signalR.HubConnectionState.Connected) {
        console.log(`Creating peer for ${otherConnId} with stream ready`);
        const peer = createPeer(otherConnId, isInitiator);
        if (peer) {
          peersRef.current.set(otherConnId, peer);
        }
      } else {
        console.warn(`Local stream or connection not ready for ${otherConnId}, delaying peer creation`);
        peerCreationPendingRef.current.set(otherConnId, true);
        const checkAndCreate = () => {
          if (peerCreationPendingRef.current.get(otherConnId) && conn.state === signalR.HubConnectionState.Connected && localStreamRef.current) {
            console.log(`Creating delayed peer for ${otherConnId}, connection state: ${conn.state}`);
            const peer = createPeer(otherConnId, isInitiator);
            if (peer) {
              peersRef.current.set(otherConnId, peer);
              peerCreationPendingRef.current.delete(otherConnId);
            }
          } else if (peerCreationPendingRef.current.get(otherConnId)) {
            console.log(`Retrying peer creation for ${otherConnId}, connection state: ${conn.state}`);
            setTimeout(checkAndCreate, 1000);
          }
        };
        setTimeout(checkAndCreate, 1000);
      }
    });

    const fetchUsers = async (retryCount = 0, maxRetries = 5) => {
      try {
        const users = await conn.invoke<Record<string, string>>('GetConnectedUsers');
        console.log('Connected users (from GetConnectedUsers):', users);
        if (users && typeof users === 'object' && Object.keys(users).length > 0) {
          setConnectedUsers(new Map(Object.entries(users)));
          setUsersInitialized(true);
          if (localStreamRef.current) {
            console.log('Stream ready on connect, notifying backend');
            await conn.invoke('SetUserReady').catch(err => console.error('Error setting user ready on connect:', err));
          }
        } else if (retryCount < maxRetries) {
          console.log(`Retrying GetConnectedUsers, attempt ${retryCount + 1}/${maxRetries}`);
          setTimeout(() => fetchUsers(retryCount + 1, maxRetries), 1000);
        } else {
          console.error('Max retries reached for GetConnectedUsers, proceeding with empty user list');
          setConnectedUsers(new Map());
          setUsersInitialized(true);
        }
      } catch (err) {
        console.error('Error invoking GetConnectedUsers:', err);
        if (retryCount < maxRetries) {
          console.log(`Retrying GetConnectedUsers, attempt ${retryCount + 1}/${maxRetries}`);
          setTimeout(() => fetchUsers(retryCount + 1, maxRetries), 1000);
        } else {
          console.error('Max retries reached for GetConnectedUsers, proceeding with empty user list');
          setConnectedUsers(new Map());
          setUsersInitialized(true);
        }
      }
    };

    conn.start()
      .then(async () => {
        setConnection(conn);
        setIsConnected(true);
        console.log('Connected to hub. ConnId:', conn.connectionId, 'State:', conn.state);
        await fetchUsers();
        const resendQueuedSignals = () => {
          signalQueueRef.current.forEach((signals, senderId) => {
            if (signals.length > 0 && conn.state === signalR.HubConnectionState.Connected) {
              console.log(`Resending ${signals.length} queued signals for ${senderId}`);
              signals.forEach(signal => {
                const json = JSON.stringify(signal);
                conn.invoke('SendSignal', json)
                  .then(() => console.log(`Resent queued signal successfully for ${senderId}: ${signal.type}`))
                  .catch(err => console.error('Failed to resend queued signal for', senderId, ':', err));
              });
              signalQueueRef.current.delete(senderId);
            }
          });
          setTimeout(resendQueuedSignals, 2000);
        };
        resendQueuedSignals();
      })
      .catch(err => {
        console.error('Error connecting to hub:', err);
        setConnectionError('Failed to connect to the Hub');
      });

    return () => {
      conn.stop().catch(err => console.error('Error stopping connection:', err));
      peersRef.current.forEach(peer => peer.destroy());
      peersRef.current.clear();
    };
  }, [user]);

  useEffect(() => {
    if (!connection || !isConnected || !usersInitialized) return;

    const setupStream = async () => {
      console.log('Requesting real camera+mic...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        console.log('Acquired local stream with tracks:', stream.getTracks());
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.volume = volume;
          localVideoRef.current.onloadedmetadata = () => {
            localVideoRef.current?.play().catch(err => console.error('Local video play failed:', err));
          };
        }
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !isMuted;
        }
      } catch (err) {
        console.warn('Error getting real camera stream:', err);
        setMediaError('Could not access camera/mic: ' + err.message);
        return;
      }

      const notifyBackend = async (retryCount = 0, maxRetries = 5) => {
        if (localStreamRef.current && connection.state === signalR.HubConnectionState.Connected) {
          console.log('Stream ready, notifying backend');
          try {
            await connection.invoke('SetUserReady');
          } catch (err) {
            console.error('Error setting user ready:', err);
            if (retryCount < maxRetries) {
              console.log(`Retrying SetUserReady, attempt ${retryCount + 1}/${maxRetries}`);
              setTimeout(() => notifyBackend(retryCount + 1, maxRetries), 1000);
            } else {
              console.error('Max retries reached for SetUserReady');
            }
          }
        } else if (retryCount < maxRetries) {
          console.log(`Stream or connection not ready, retrying notification, attempt ${retryCount + 1}/${maxRetries}`);
          setTimeout(() => notifyBackend(retryCount + 1, maxRetries), 1000);
        } else {
          console.error('Max retries reached for notifying backend');
        }
      };

      await notifyBackend();
    };

    if (!localStreamRef.current) {
      setupStream();
    }

    return () => {
      if (localStreamRef.current) {
        const audioTrack = localStreamRef.current.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !isMuted;
        }
      }
    };
  }, [connection, isConnected, usersInitialized, isMuted]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isMuted;
        setIsMuted(!isMuted);
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (localVideoRef.current) {
      localVideoRef.current.volume = newVolume;
    }
  };

  const createPeer = (senderId: string, initiator: boolean): SimplePeer.Instance => {
    console.log(`Creating peer for ${senderId}, initiator = ${initiator}, connection state: ${connection?.state}`);
    if (!localStreamRef.current) {
      console.error(`Cannot create peer for ${senderId}: local stream not ready`);
      return null;
    }
    const peer = new SimplePeer({
      initiator,
      trickle: true,
      stream: localStreamRef.current,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject',
          },
          {
            urls: 'turn:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject',
          },
        ],
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 1,
      },
    });

    peer.on('signal', (data) => {
      const json = JSON.stringify(data);
      console.log(`Peer generated signal for ${senderId}:`, data.type, '| length:', json.length, 'connection state:', connection?.state);
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke('SendSignal', json)
          .then(() => console.log(`Signal sent successfully for ${senderId}: ${data.type}`))
          .catch(err => {
            console.error('SendSignal error for', senderId, ':', err);
            signalQueueRef.current.set(senderId, (signalQueueRef.current.get(senderId) || []).concat([data]));
          });
      } else {
        console.warn('SignalR connection not available or not connected, queuing signal:', data.type);
        signalQueueRef.current.set(senderId, (signalQueueRef.current.get(senderId) || []).concat([data]));
      }
    });

    peer.on('connect', () => {
      console.log(`Peer connected with ${senderId}! WebRTC established.`);
      const queuedSignals = signalQueueRef.current.get(senderId) || [];
      if (queuedSignals.length > 0) {
        console.log(`Processing ${queuedSignals.length} queued signals for ${senderId} after connect`);
        queuedSignals.forEach(signal => peer.signal(signal));
        signalQueueRef.current.delete(senderId);
        queuedSignals.forEach(signal => {
          const json = JSON.stringify(signal);
          connection.invoke('SendSignal', json)
            .then(() => console.log(`Queued signal sent successfully for ${senderId}: ${signal.type}`))
            .catch(err => console.error('Failed to send queued signal for', senderId, ':', err));
        });
      }
    });

    let retryCount = 0;
    const maxRetries = 5;
    const retryInterval = 2000;
    const sendQueuedSignals = () => {
      if (!peer._pc || peer._pc.iceConnectionState !== 'connected') {
        if (retryCount < maxRetries) {
          console.warn(`Connect event did not fire for ${senderId}, retry ${retryCount + 1}/${maxRetries}`);
          retryCount++;
          setTimeout(sendQueuedSignals, retryInterval);
        } else {
          console.warn(`Max retries reached for ${senderId}, manually sending queued signals`);
          const queuedSignals = signalQueueRef.current.get(senderId) || [];
          if (queuedSignals.length > 0 && connection && connection.state === signalR.HubConnectionState.Connected) {
            console.log(`Sending ${queuedSignals.length} queued signals for ${senderId}`);
            queuedSignals.forEach(signal => {
              const json = JSON.stringify(signal);
              connection.invoke('SendSignal', json)
                .then(() => console.log(`Queued signal sent successfully for ${senderId}: ${signal.type}`))
                .catch(err => console.error('Failed to send queued signal for', senderId, ':', err));
            });
            signalQueueRef.current.delete(senderId);
          }
        }
      }
    };
    setTimeout(sendQueuedSignals, 5000);

    peer.on('stream', (remoteStream) => {
      console.log(`Received remote stream from ${senderId} with tracks:`, remoteStream.getTracks().map(t => `${t.kind}:${t.label}`));
      const hasVideo = remoteStream.getVideoTracks().length > 0;
      const container = document.getElementById('remote-videos');
      if (!container) {
        console.error('No remote-videos container found.');
        return;
      }

      // Remove any existing element for this sender
      const existingElement = remoteVideosRef.current.get(senderId);
      if (existingElement) {
        existingElement.remove();
        remoteVideosRef.current.delete(senderId);
      }

      if (hasVideo) {
        const newVideo = document.createElement('video');
        newVideo.id = `remote-video-${senderId}`;
        newVideo.srcObject = remoteStream;
        newVideo.autoplay = true;
        newVideo.playsInline = true;
        newVideo.style.width = '300px'; // Fixed width
        newVideo.style.height = '200px'; // Fixed height
        newVideo.style.objectFit = 'cover'; // Ensure video fills the box
        newVideo.style.border = '1px solid #ccc';
        newVideo.onloadedmetadata = () => {
          newVideo.play().catch(err => console.error(`Remote video play error for ${senderId}:`, err));
        };
        container.appendChild(newVideo);
        remoteVideosRef.current.set(senderId, newVideo);
      } else {
        const placeholder = document.createElement('div');
        placeholder.id = `remote-placeholder-${senderId}`;
        placeholder.style.width = '300px'; // Fixed width
        placeholder.style.height = '200px'; // Fixed height
        placeholder.style.backgroundColor = '#000';
        placeholder.style.display = 'flex';
        placeholder.style.alignItems = 'center';
        placeholder.style.justifyContent = 'center';
        placeholder.style.color = '#fff';
        placeholder.style.border = '1px solid #ccc';
        placeholder.innerText = 'No Video';
        container.appendChild(placeholder);
        remoteVideosRef.current.set(senderId, placeholder);
      }
    });

    peer.on('iceConnectionStateChange', () => {
      if (peer._pc) {
        console.log(`ICE connection state for ${senderId}:`, peer._pc.iceConnectionState);
        if (peer._pc.iceConnectionState === 'failed') {
          console.warn(`ICE connection failed for ${senderId}, attempting renegotiation`);
          peer.signal({ renegotiate: true });
        } else if (peer._pc.iceConnectionState === 'connected' || peer._pc.iceConnectionState === 'completed') {
          console.log(`ICE negotiation completed successfully for ${senderId}`);
        }
      }
    });

    peer.on('icecandidate', (event) => {
      console.log(`ICE candidate for ${senderId}:`, event.candidate ? event.candidate.candidate : 'end-of-candidates');
    });

    peer.on('negotiationneeded', () => {
      console.log(`Negotiation needed for ${senderId}`);
    });

    peer.on('error', (err) => {
      console.error(`Peer error for ${senderId}:`, err);
    });

    peer.on('close', () => {
      console.log(`Peer closed for ${senderId}. Removing remote video.`);
      peer.destroy();
      peersRef.current.delete(senderId);
      const element = remoteVideosRef.current.get(senderId);
      if (element) {
        element.remove();
        remoteVideosRef.current.delete(senderId);
      }
    });

    return peer;
  };

  const handleSendMessage = () => {
    if (!connection || !isConnected) return;
    if (!chatInput.trim()) return;
    connection.invoke('SendChatMessage', chatInput)
      .then(() => setChatInput(''))
      .catch(err => console.error('SendChatMessage error:', err));
  };

  const fetchUsers = async (retryCount = 0, maxRetries = 5) => {
    try {
      const users = await connection.invoke<Record<string, string>>('GetConnectedUsers');
      console.log('Connected users (from GetConnectedUsers):', users);
      if (users && typeof users === 'object' && Object.keys(users).length > 0) {
        setConnectedUsers(new Map(Object.entries(users)));
        setUsersInitialized(true);
        if (localStreamRef.current) {
          console.log('Stream ready on connect, notifying backend');
          await connection.invoke('SetUserReady').catch(err => console.error('Error setting user ready on connect:', err));
        }
      } else if (retryCount < maxRetries) {
        console.log(`Retrying GetConnectedUsers, attempt ${retryCount + 1}/${maxRetries}`);
        setTimeout(() => fetchUsers(retryCount + 1, maxRetries), 1000);
      } else {
        console.error('Max retries reached for GetConnectedUsers, proceeding with empty user list');
        setConnectedUsers(new Map());
        setUsersInitialized(true);
      }
    } catch (err) {
      console.error('Error invoking GetConnectedUsers:', err);
      if (retryCount < maxRetries) {
        console.log(`Retrying GetConnectedUsers, attempt ${retryCount + 1}/${maxRetries}`);
        setTimeout(() => fetchUsers(retryCount + 1, maxRetries), 1000);
      } else {
        console.error('Max retries reached for GetConnectedUsers, proceeding with empty user list');
        setConnectedUsers(new Map());
        setUsersInitialized(true);
      }
    }
  };

  if (!user) {
    return <div>Please log in to access the video room.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px' }}>
      <h1>Automatic Connect Video Room (Multi-User)</h1>
      {mediaError && <p style={{ color: 'red' }}>{mediaError}</p>}
      {connectionError && <p style={{ color: 'red' }}>{connectionError}</p>}
      {!isConnected && !connectionError && <p style={{ color: 'orange' }}>Connecting to server...</p>}

      <div style={{ display: 'flex', gap: '20px' }}>
        <div>
          <h3>Your Video</h3>
          <video
            ref={localVideoRef}
            autoPlay
            muted={isMuted}
            playsInline
            style={{ width: '300px', height: '200px', objectFit: 'cover', border: '1px solid #ccc', margin: '10px' }}
          />
          <div style={{ marginTop: '10px' }}>
            <button onClick={toggleMute} style={{ padding: '5px 10px', marginRight: '10px' }}>
              {isMuted ? 'Unmute' : 'Mute'}
            </button>
            <label>
              Volume:
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                style={{ marginLeft: '10px', verticalAlign: 'middle' }}
              />
            </label>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <h3>Remote Videos</h3>
          <div
            id="remote-videos"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
              justifyContent: 'flex-start',
              backgroundColor: remoteVideosRef.current.size > 0 ? 'transparent' : '#000',
              color: '#fff',
              minHeight: '200px',
            }}
          >
            {remoteVideosRef.current.size === 0 && <span>Waiting for remote videos...</span>}
          </div>
        </div>
      </div>

      <div>
        <p><em>Connections are automatic when users join.</em></p>
      </div>

      <div style={{
        border: '1px solid #ccc',
        padding: '10px',
        height: '150px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column-reverse'
      }}>
        {messages.slice().reverse().map((m, i) => (
          <p key={i}><strong>{m.senderId}:</strong> {m.message}</p>
        ))}
      </div>

      <div>
        <input
          type="text"
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
          placeholder="Type a message..."
          style={{ width: '300px', padding: '5px' }}
          disabled={!isConnected}
        />
        <button
          onClick={handleSendMessage}
          disabled={!isConnected}
          style={{ padding: '5px 10px', marginLeft: '10px' }}
        >
          Send
        </button>
      </div>

      <div>
        <h3>Connected Users:</h3>
        {connectedUsers.size > 0 ? (
          <ul>
            {Array.from(connectedUsers.entries()).map(([connId, name]) => (
              <li key={connId}>{name || 'Unknown'} (ID: {connId})</li>
            ))}
          </ul>
        ) : (
          <p>No other users connected.</p>
        )}
      </div>
    </div>
  );
};

export default VideoRoom;